import { IPositionsState } from './CreateProjectOpportunity';
import { ColumnBoxFlexFullWidth, FlexBoxFullWidth } from '../../common/boxes';
import { IconButton, Tooltip, Typography } from '@mui/material';
import { TEXTS } from '../../../core/texts';
import { Add } from '@mui/icons-material';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { ICreateProjectPosition } from '../../../interfaces/projectPosition';
import ProjectPositionFormItem from '../../common/project-positions/ProjectPositionFormItem';
import { ValidationErrorState } from '../../../types/validationErrorState';
import { PaymentRateType } from '../../../enums/paymentRateType';
import { CURRENCY_CODES } from '../../../core/currencies';
import { AvailabilityType } from '../../../enums/AvailabilityType';
import { PROFICIENCY_DESCRIPTIONS } from '../../../interfaces/language';
import { useGetLanguageCodes } from '../../../queryHooks/useGetLanguageCodes';
import { useGetPositionsWithSkills } from '../../../queryHooks/useGetPositionsWithSkills';

interface IProps {
  positions: IPositionsState;
  setPositions: Dispatch<SetStateAction<IPositionsState>>;
  validationErrors: { [key: string]: ValidationErrorState };
  setValidationErrors: Dispatch<SetStateAction<{ [key: string]: ValidationErrorState }>>;
};

export const initialPosition: ICreateProjectPosition = {
  roleId: 1,
  positionId: 1,
  title: '',
  description: '',
  link: '',
  preferredRate: {
    amount: 0,
    type: PaymentRateType.HOURLY,
    currency: CURRENCY_CODES[0]
  },
  availability: {
    fromHour: 10,
    toHour: 18,
    timezone: '',
    availabilityType: '' as AvailabilityType
  },
  language: {
    languageCode: 'en',
    minimumProficiencyLevel: PROFICIENCY_DESCRIPTIONS.PROFESSIONAL_WORKING as PROFICIENCY_DESCRIPTIONS
  },
  skills: []
};

let POSITION_KEY = 0;

const CreatePositions = ({ positions, setPositions, validationErrors, setValidationErrors }: IProps) => {
  const { data: positionsWithSkills } = useGetPositionsWithSkills();
  const { data: languageCodes } = useGetLanguageCodes();
  const addNewPosition = useCallback(() => {
    // This setTimeout is used to avoid app crashing in case of very fast clicking 'add' button
    // in terms of a bit slowly "position" timezone pre-population 
    setTimeout(() => {
      setPositions(prev => ({ ...prev, [++POSITION_KEY]: initialPosition }));
    },0);
    
    if (validationErrors.emptyPositions) {
      setValidationErrors(prev => {
        const { emptyPositions, ...rest } = prev;
        return rest;
      });
    }
  }, [setPositions, validationErrors?.emptyPositions, setValidationErrors]);

  return (
    <ColumnBoxFlexFullWidth>
      <FlexBoxFullWidth alignItems={'center'} justifyContent={'space-between'}>
        <Typography color={'primary'}>{TEXTS.POSITIONS}</Typography>
        <Tooltip title={TEXTS.ADD_POSITION}>
          <IconButton color={'primary'} onClick={addNewPosition}>
            <Add/>
          </IconButton>
        </Tooltip>
      </FlexBoxFullWidth>
      <ColumnBoxFlexFullWidth>
        {
          Object.entries(positions).map(([key, position]) => (
            <ProjectPositionFormItem
              key={key}
              position={position}
              positionKey={key}
              setPositions={setPositions}
              positionsWithSkills={positionsWithSkills || []}
              languageCodes={languageCodes || []}
              validationErrors={validationErrors[key]}
            />
          ))
        }
      </ColumnBoxFlexFullWidth>
    </ColumnBoxFlexFullWidth>
  );
};

export default CreatePositions;